/**
 * RC-117 Phase 1 + RC-117b Phase 1 — Workflow Versions slideout.
 *
 * Phase 1 (RC-117) shipped the metadata list + read-only JSON view.
 * Phase 2 (RC-117b) layers on:
 *   • Structural diff between any two versions (canvas-aware:
 *     nodesAdded / nodesRemoved / nodesChanged / edgesAdded /
 *     edgesRemoved with the offending ids surfaced).
 *   • "Roll back to this version" action that POSTs to the BE
 *     rollback endpoint and surfaces the new version number.
 *
 * State model:
 *   selectedA   — primary version (the one operator clicked first)
 *   selectedB   — secondary; populated by the "Compare to this"
 *                 button on another row. When both are set we render
 *                 the diff between A (older) and B (newer). When
 *                 only A is set we render the read-only JSON view
 *                 (RC-117 Phase 1 behavior).
 *
 * RC-117c (deferred):
 *   • Wire change_kind at each save site (ai_build / ai_fix /
 *     import / rollback uses 'rollback' kind — already done here).
 *   • Concurrent-edit awareness via polling / SSE.
 *
 * No engineering jargon in operator-visible strings
 * (feedback_no_engineering_jargon_in_ui).
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { History } from 'lucide-react';
import { Slideout } from '../../components/v2/Slideout';
import {
  listWorkflowVersions,
  getWorkflowVersion,
  rollbackWorkflowVersion,
} from '../../services/api';
// RC-118b — diffDefinitions promoted into a shared util so useCanvas
// can reuse the same comparison logic to derive per-op undo labels.
// The slideout's prior inlined implementation has been removed in
// favour of this single source of truth.
import { diffDefinitions } from '../../utils/canvas/diffDefinitions';

function fmtAgo(iso) {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return '';
  const sec = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function fmtAbsolute(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function shortId(uuid) {
  if (!uuid || typeof uuid !== 'string') return '—';
  return uuid.split('-')[0];
}

/* RC-147 — render order for "Saved by ..." line.
 *   1. createdByName (BE-resolved display name via DisplayNameService)
 *   2. shortId(createdBy) as forensic fallback only when the user can't
 *      be resolved (deleted account / system save with no row).
 *   3. "system" when createdBy is null (background rollback /
 *      orchestrator-initiated save).
 * No raw UUID-prefix leakage in the happy path — matches the platform's
 * R1/R4 display conventions (no jargon on surfaces). */
function savedByLabel(row) {
  if (row?.createdByName && String(row.createdByName).trim().length > 0) {
    return row.createdByName;
  }
  if (row?.createdBy) return shortId(row.createdBy);
  return 'system';
}

function changeKindLabel(kind) {
  if (!kind || kind === 'manual_save') return 'Manual save';
  if (kind === 'ai_build') return 'AI build';
  if (kind === 'ai_fix') return 'AI fix';
  if (kind === 'import') return 'Imported';
  if (kind === 'rollback') return 'Rollback';
  return kind;
}

/* RC-147 — kind → branded chip tone. Manual saves are the neutral
 * majority (navy); AI builds/fixes get a distinct accent so operators
 * can spot machine-authored revisions at a glance; rollbacks read as
 * warning-tone so an operator scanning history sees the intervention
 * line clearly. */
function changeKindChipTone(kind) {
  if (kind === 'ai_build' || kind === 'ai_fix') {
    return { bg: 'var(--v2-info-soft, rgba(99,102,241,.12))', fg: 'var(--v2-action)' };
  }
  if (kind === 'rollback') {
    return { bg: 'var(--v2-warn-soft)', fg: 'var(--v2-accent-amber)' };
  }
  if (kind === 'import') {
    return { bg: 'var(--v2-bg-elevated)', fg: 'var(--v2-text-secondary)' };
  }
  // manual_save + unknown → neutral chip.
  return { bg: 'var(--v2-bg-elevated)', fg: 'var(--v2-text-secondary)' };
}

function DiffSection({ title, tone, items, render, emptyText }) {
  if (!items || items.length === 0) {
    if (!emptyText) return null;
    return (
      <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', padding: '4px 8px' }}>
        {emptyText}
      </div>
    );
  }
  const bgByTone = {
    add: 'var(--v2-success-soft)',
    remove: 'var(--v2-danger-soft)',
    change: 'var(--v2-bg-elevated)',
  }[tone] || 'var(--v2-bg-elevated)';
  const colorByTone = {
    add: 'var(--v2-success)',
    remove: 'var(--v2-accent-rose)',
    change: 'var(--v2-text-secondary)',
  }[tone] || 'var(--v2-text-secondary)';
  return (
    <div style={{
      marginBottom: 8, padding: '6px 8px',
      background: bgByTone, borderRadius: 6,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: colorByTone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {title} · {items.length}
      </div>
      <ul style={{ margin: 0, padding: '0 0 0 14px', fontSize: 11, color: 'var(--v2-text-secondary)' }}>
        {items.map((it, i) => <li key={i} style={{ marginBottom: 2 }}>{render(it)}</li>)}
      </ul>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.workflowId
 * @param {string} [props.workflowName]
 * @param {(opts: {newVersion: number, target: number}) => void} [props.onRolledBack]
 *   Called after a successful rollback so the parent canvas can
 *   reload the workflow at the new version. Optional; slideout
 *   still updates its own list either way.
 */
export default function WorkflowVersionsSlideout({
  isOpen, onClose, workflowId, workflowName, onRolledBack,
}) {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [versions, setVersions] = useState([]);

  // Dual selection — primary (A) and optional secondary (B). When both
  // are non-null, we render the structural diff between them; when
  // only A is set, we fall back to the RC-117 Phase 1 read-only view.
  const [selectedA, setSelectedA] = useState(null);
  const [selectedB, setSelectedB] = useState(null);
  const [defA, setDefA] = useState(null);
  const [defB, setDefB] = useState(null);
  const [defLoading, setDefLoading] = useState(false);

  // Rollback state.
  //   confirmVersion — operator clicked "Roll back to vN"; we replace
  //                    the button with an inline "Are you sure?" pair
  //                    (danger + ghost) rather than firing window.confirm
  //                    (per memory feedback_no_browser_confirm — the
  //                    inline confirm matches the rest of V2 chrome).
  //   busyVersion    — rollback is in-flight for vN; both inline buttons
  //                    disable until the POST resolves.
  const [confirmVersion, setConfirmVersion] = useState(null);
  const [rollbackBusyVersion, setRollbackBusyVersion] = useState(null);
  const [rollbackError, setRollbackError] = useState(null);

  /* RC-117c — Concurrent-edit awareness.
   *
   * Why: two operators editing the same workflow in parallel will each
   * see "the latest version" was vN at the moment they opened the
   * slideout. If the other side saves while this slideout is open, the
   * list is silently stale — operator might roll back to vN thinking
   * it's the head when it's actually one save behind. We don't have
   * optimistic-lock save-conflict yet (separate open follow-up), but
   * we can at least SURFACE staleness so the operator hits Reload.
   *
   * Mechanism: capture the top version at open-time and poll list every
   * 30s while open; if a newer version appears, set newerVersionAvailable.
   * Banner offers a one-click Reload. SSE upgrade is a Phase 2.
   *
   * The poll is gated on `isOpen` so closed slideouts incur zero load.
   * Bail-out on any error keeps the polling silent — a transient 5xx
   * shouldn't flash a misleading "newer version" banner. */
  const [openedAtTopVersion, setOpenedAtTopVersion] = useState(null);
  const [newerVersionAvailable, setNewerVersionAvailable] = useState(false);

  const reload = useCallback(() => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    listWorkflowVersions(workflowId)
      .then((res) => {
        if (!res || !res.ok) {
          setError(res?.error || 'Could not load version history.');
          setVersions([]);
          return;
        }
        const list = Array.isArray(res.data?.versions) ? res.data.versions : [];
        setVersions(list);
      })
      .catch((e) => setError(e?.message || 'Could not load version history.'))
      .finally(() => setLoading(false));
  }, [workflowId]);

  // Fresh fetch on every open.
  useEffect(() => {
    if (!isOpen || !workflowId) return;
    setSelectedA(null);
    setSelectedB(null);
    setDefA(null);
    setDefB(null);
    setRollbackError(null);
    setNewerVersionAvailable(false); // RC-117c — clear stale banner on re-open
    setOpenedAtTopVersion(null);     // captured by the next reload's success path
    reload();
  }, [isOpen, workflowId, reload]);

  /* RC-117c — Anchor the open-time top version once the initial load
   * lands. Subsequent polls compare against this anchor, NOT the
   * current top of the list — otherwise a reload would slide the
   * anchor forward and we'd never detect staleness. */
  useEffect(() => {
    if (!isOpen) return;
    if (openedAtTopVersion !== null) return; // already anchored
    if (!versions || versions.length === 0) return;
    const top = Math.max(...versions.map((v) => Number(v.version) || 0));
    if (top > 0) setOpenedAtTopVersion(top);
  }, [isOpen, versions, openedAtTopVersion]);

  /* RC-117c — Poll for newer versions every 30s while the slideout is
   * open. Cheap (one GET); the BE list endpoint is already paginated
   * and tenant-scoped. We compare against the open-time anchor so
   * staleness sticks until the operator reloads. The poll is silent
   * on error — we don't want a transient 5xx to flash the banner. */
  useEffect(() => {
    if (!isOpen || !workflowId || openedAtTopVersion === null) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await listWorkflowVersions(workflowId);
        if (cancelled) return;
        if (!res || !res.ok) return;
        const list = Array.isArray(res.data?.versions) ? res.data.versions : [];
        if (list.length === 0) return;
        const remoteTop = Math.max(...list.map((v) => Number(v.version) || 0));
        if (remoteTop > openedAtTopVersion) {
          setNewerVersionAvailable(true);
        }
      } catch { /* silent — see comment block above */ }
    };
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isOpen, workflowId, openedAtTopVersion]);

  /* RC-117c — Reload that ALSO clears the newer-version banner and
   * re-anchors openedAtTopVersion to the freshly-loaded top. Bound to
   * the inline "Reload" button in the banner. */
  const reloadAndReanchor = useCallback(() => {
    setNewerVersionAvailable(false);
    setOpenedAtTopVersion(null); // re-anchored by the post-reload effect
    reload();
  }, [reload]);

  // Pull a single version's full definition (used for both panel views
  // AND for the diff computation).
  const fetchDef = useCallback(async (version) => {
    const res = await getWorkflowVersion(workflowId, version);
    if (!res || !res.ok) return { __error: res?.error || 'Could not load that version.' };
    return res.data || null;
  }, [workflowId]);

  // When selection changes, fetch the corresponding def(s).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedA) { setDefA(null); return; }
      setDefLoading(true);
      const d = await fetchDef(selectedA.version);
      if (!cancelled) setDefA(d);
      setDefLoading(false);
    })();
    return () => { cancelled = true; };
  }, [selectedA, fetchDef]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedB) { setDefB(null); return; }
      setDefLoading(true);
      const d = await fetchDef(selectedB.version);
      if (!cancelled) setDefB(d);
      setDefLoading(false);
    })();
    return () => { cancelled = true; };
  }, [selectedB, fetchDef]);

  // Compute diff when BOTH versions are present. Older version = A,
  // newer = B so "added in B" reads as "what THIS new version added".
  const diff = useMemo(() => {
    if (!selectedA || !selectedB) return null;
    if (!defA || !defB || defA.__error || defB.__error) return null;
    const older = selectedA.version < selectedB.version ? defA : defB;
    const newer = selectedA.version < selectedB.version ? defB : defA;
    return diffDefinitions(older?.definition, newer?.definition);
  }, [selectedA, selectedB, defA, defB]);

  const onPickRow = useCallback((row) => {
    /* RC-147 — Auto-diff-against-predecessor.
     *
     * The original RC-117b design required the operator to click row A,
     * then click row B before any diff would render. With only 2 versions
     * and no visible "diff" CTA, the gesture was undiscoverable — the
     * slideout looked like a flat timeline with no per-version detail
     * (operator complaint on the 2026-06-19 RC-147 callout).
     *
     * New semantics — descriptive details show on the very first click:
     *   • No selection yet     → set the clicked row as A, AND
     *                            auto-set B to its immediately-previous
     *                            version when one exists. This is the
     *                            "what changed in this save" diff —
     *                            the answer the operator actually wants.
     *                            When no predecessor exists (oldest
     *                            historical version), B stays null and
     *                            we fall back to the read-only JSON view.
     *   • A set, click same    → clear (collapse panel).
     *   • A set, click another → REPLACE B with the click (operator-
     *                            overridden comparison target). This
     *                            preserves the dual-pick power user
     *                            workflow without burying single-click
     *                            descriptive diff.
     *   • A+B set, click A     → collapse.
     *   • A+B set, click B     → swap B back to the predecessor of A
     *                            (treat as deselecting B). */
    if (!row || !row.version) return;
    setRollbackError(null);

    // `versions` is ordered newest-first by the BE list endpoint.
    // The predecessor of version N is the next-older row in the list.
    const predecessorOf = (v) => {
      const idx = versions.findIndex((x) => x.version === v.version);
      if (idx < 0) return null;
      const prev = versions[idx + 1];
      return prev || null;
    };

    if (!selectedA) {
      setSelectedA(row);
      const prev = predecessorOf(row);
      setSelectedB(prev); // null when row is the oldest known version
      return;
    }
    if (selectedA.version === row.version) {
      setSelectedA(null); setSelectedB(null);
      return;
    }
    if (selectedB && selectedB.version === row.version) {
      // Operator clicked the current B — treat as "drop B" and fall back
      // to auto-predecessor of A (or null if none).
      setSelectedB(predecessorOf(selectedA));
      return;
    }
    // Operator overriding the comparison target.
    setSelectedB(row);
  }, [selectedA, selectedB, versions]);

  const onRollback = useCallback(async (row) => {
    if (!row || !row.version) return;
    setConfirmVersion(null);
    setRollbackBusyVersion(row.version);
    setRollbackError(null);
    try {
      const res = await rollbackWorkflowVersion(workflowId, row.version);
      if (!res || !res.ok) {
        const issues = Array.isArray(res?.data?.issues) ? res.data.issues : [];
        const issueText = issues.length > 0
          ? ` (${issues.length} issue${issues.length === 1 ? '' : 's'} blocked the rollback — open the workflow to see details)`
          : '';
        setRollbackError((res?.data?.error || res?.error || 'Could not roll back.') + issueText);
        return;
      }
      const newVersion = Number(res.data?.newVersion) || null;
      if (typeof onRolledBack === 'function' && newVersion) {
        onRolledBack({ newVersion, target: row.version });
      }
      // Reload list to surface the new rollback row at the top.
      reload();
      // Clear selection so the operator sees the fresh list.
      setSelectedA(null);
      setSelectedB(null);
      setDefA(null);
      setDefB(null);
    } catch (e) {
      setRollbackError(e?.message || 'Could not roll back.');
    } finally {
      setRollbackBusyVersion(null);
    }
  }, [workflowId, reload, onRolledBack]);

  /* RC-147 — Branded header.
   *
   * Replaces the prior plain-string header (which rendered as 14px
   * default-styled text and read as "lazy / no Prove7 chrome" on the
   * 2026-06-19 callout). Uses the same icon-+-title pattern as the
   * Compliance Certificates / Reports slideouts so the version
   * history panel sits inside the platform's visual system rather
   * than looking like a debug overlay.
   *
   * Counter chip mirrors the inventory page badges so an operator can
   * tell at a glance how dense the history is before they start
   * scrolling. */
  const headerJsx = (
    <div className="flex flex-col gap-1 pr-8">
      <h2 className="text-[15px] font-bold m-0 inline-flex items-center gap-2 text-v2-ink">
        <History size={16} className="text-v2-action" aria-hidden="true" />
        Version history
      </h2>
      {workflowName && (
        <div className="text-[12px] text-v2-ink-muted truncate" title={workflowName}>
          {workflowName}
        </div>
      )}
    </div>
  );

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      header={headerJsx}
      width={560}
      ariaLabel="Workflow version history"
    >
      <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--v2-text-primary)' }}>
        {loading && (
          <div style={{ color: 'var(--v2-text-muted)' }}>Loading version history…</div>
        )}
        {error && (
          <div style={{
            padding: '8px 10px', borderRadius: 6, fontSize: 12,
            background: 'var(--v2-danger-soft)', color: 'var(--v2-accent-rose)',
            border: '1px solid var(--v2-accent-rose-soft)', marginBottom: 8,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && versions.length === 0 && (
          <div style={{ color: 'var(--v2-text-muted)' }}>
            No prior versions yet. New saves of this workflow will start showing up here.
          </div>
        )}

        {/* RC-117c — Newer-version-available banner.
            Fires only when the 30s poll detects a higher version
            number than the open-time anchor. Operator-visible string is
            deliberately jargon-free (no "version N+1 detected" — just
            the action they should take). */}
        {newerVersionAvailable && (
          <div style={{
            padding: '8px 10px', marginBottom: 8, borderRadius: 6,
            background: 'var(--v2-warn-soft)', color: 'var(--v2-accent-amber)',
            border: '1px solid var(--v2-accent-amber-soft)',
            fontSize: 12, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 8,
          }}>
            <span>This workflow was saved again since you opened this panel.</span>
            <button
              type="button"
              onClick={reloadAndReanchor}
              style={{
                padding: '4px 10px', borderRadius: 4,
                border: '1px solid var(--v2-accent-amber-soft)',
                background: 'var(--v2-bg-elevated)',
                color: 'var(--v2-text-primary)',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Reload
            </button>
          </div>
        )}

        {!loading && !error && versions.length > 0 && (
          <>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--v2-text-muted)',
              marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>
              {versions.length} version{versions.length === 1 ? '' : 's'}
              {/* RC-147 — auto-diff means a single click already
                  populates A+B. Inform the operator how to override the
                  comparison target rather than telling them they still
                  need to click. */}
              {selectedA && selectedB && ` — comparing v${Math.min(selectedA.version, selectedB.version)} → v${Math.max(selectedA.version, selectedB.version)} · click any other row to change`}
              {selectedA && !selectedB && versions.length > 1 && ` — viewing v${selectedA.version} (oldest known version; nothing earlier to compare)`}
              {selectedA && !selectedB && versions.length === 1 && ` — viewing the only saved version`}
              {!selectedA && versions.length > 1 && ' — click a version to see what changed'}
              {!selectedA && versions.length === 1 && ' — first save; no prior history to compare yet'}
            </div>

            <ul style={{
              listStyle: 'none', padding: 0, margin: 0,
              borderTop: '1px solid var(--v2-border-default)',
            }}>
              {versions.map((v, rowIdx) => {
                const isA = selectedA && selectedA.version === v.version;
                const isB = selectedB && selectedB.version === v.version;
                const active = isA || isB;
                const role = isA ? 'A' : (isB ? 'B' : null);
                // RC-147 — the BE returns rows newest-first; the row at
                // index 0 is the current canvas state ("Latest"), which
                // shapes both the per-row Latest pill and the rollback
                // affordance further down.
                const isLatest = rowIdx === 0;
                const chipTone = changeKindChipTone(v.changeKind);
                return (
                  <li
                    key={v.id || `v_${v.version}`}
                    style={{
                      padding: '12px 10px',
                      borderBottom: '1px solid var(--v2-border-default)',
                      // RC-147 — selected rows get a thin left navy
                      // accent so the comparison pair is immediately
                      // scannable against unselected rows.
                      borderLeft: active
                        ? '3px solid var(--v2-action)'
                        : '3px solid transparent',
                      background: active ? 'var(--v2-bg-elevated)' : 'transparent',
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}
                  >
                    <div
                      onClick={() => onPickRow(v)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    >
                      <strong style={{ fontSize: 13, color: 'var(--v2-text-primary)' }}>v{v.version}</strong>
                      {isLatest && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
                          color: 'var(--v2-success)', background: 'var(--v2-success-soft)',
                          textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>Latest</span>
                      )}
                      {role && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                          color: 'var(--v2-bg-surface)', background: 'var(--v2-action)',
                        }} title={isA ? 'Comparison base (A)' : 'Comparison target (B)'}>{role}</span>
                      )}
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        padding: '1px 6px', borderRadius: 3,
                        color: chipTone.fg, background: chipTone.bg,
                      }}>
                        {changeKindLabel(v.changeKind)}
                      </span>
                      <span style={{
                        marginLeft: 'auto', fontSize: 11, color: 'var(--v2-text-muted)',
                      }} title={fmtAbsolute(v.createdAt)}>
                        {fmtAgo(v.createdAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                      {/* RC-147 — prefer the BE-resolved display name;
                          shortId is now a forensic fallback used only
                          for deleted users with no resolvable row. */}
                      Saved by{' '}
                      <span
                        style={{ color: 'var(--v2-text-secondary)', fontWeight: 500 }}
                        title={v.createdBy || undefined}
                      >
                        {savedByLabel(v)}
                      </span>
                      {v.note ? ` — ${v.note}` : ''}
                    </div>
                    {/* RC-117b — Rollback affordance per row. Disabled
                        on the latest version (= current state; nothing
                        to roll back TO that's different). Two-step
                        inline confirm replaces window.confirm per
                        feedback_no_browser_confirm. */}
                    {v.version !== versions[0]?.version && (
                      <div style={{ marginTop: 4 }}>
                        {confirmVersion === v.version ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11, color: 'var(--v2-text-secondary)' }}>
                              Roll back to v{v.version}? A new version will be created on top.
                            </span>
                            <button
                              type="button"
                              disabled={rollbackBusyVersion === v.version}
                              onClick={() => onRollback(v)}
                              style={{
                                fontSize: 10, padding: '3px 8px',
                                border: 'none', borderRadius: 4,
                                cursor: rollbackBusyVersion === v.version ? 'default' : 'pointer',
                                background: 'var(--v2-danger)',
                                color: 'var(--v2-bg-surface)',
                                fontWeight: 600,
                              }}
                            >
                              {rollbackBusyVersion === v.version ? 'Rolling back…' : 'Confirm rollback'}
                            </button>
                            <button
                              type="button"
                              disabled={rollbackBusyVersion === v.version}
                              onClick={() => setConfirmVersion(null)}
                              style={{
                                fontSize: 10, padding: '3px 8px',
                                background: 'transparent',
                                border: '1px solid var(--v2-border-default)',
                                borderRadius: 4, cursor: 'pointer',
                                color: 'var(--v2-text-secondary)',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={rollbackBusyVersion != null}
                            onClick={() => setConfirmVersion(v.version)}
                            style={{
                              fontSize: 10, padding: '3px 8px',
                              border: '1px solid var(--v2-border-default)',
                              borderRadius: 4, cursor: rollbackBusyVersion != null ? 'default' : 'pointer',
                              background: 'var(--v2-bg-surface)',
                              color: 'var(--v2-text-secondary)',
                              opacity: rollbackBusyVersion != null ? 0.5 : 1,
                            }}
                          >
                            Roll back to v{v.version}
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {rollbackError && (
              <div style={{
                marginTop: 10, padding: '8px 10px', borderRadius: 6, fontSize: 12,
                background: 'var(--v2-danger-soft)', color: 'var(--v2-accent-rose)',
                border: '1px solid var(--v2-accent-rose-soft)',
              }}>
                {rollbackError}
              </div>
            )}

            {/* Detail panel — diff if A+B selected, otherwise read-only def for A */}
            {selectedA && (
              <div style={{
                marginTop: 12, padding: '10px 12px', borderRadius: 6,
                background: 'var(--v2-bg-elevated)',
                border: '1px solid var(--v2-border-default)',
              }}>
                {defLoading && (
                  <div style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>Loading…</div>
                )}

                {!defLoading && selectedA && !selectedB && defA && !defA.__error && (
                  <>
                    {/* RC-147 — Reached only when the operator clicks the
                        OLDEST recorded version (no predecessor to auto-
                        diff against). We show the full snapshot so the
                        starting state is still inspectable; the read-
                        only JSON dump is an intentional fallback, not
                        the default detail surface anymore. */}
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                      Snapshot at v{selectedA.version}
                      {versions.length > 1 ? ' (oldest known version)' : ''}
                    </div>
                    <pre style={{
                      margin: 0, fontSize: 11, lineHeight: 1.45,
                      maxHeight: 360, overflow: 'auto',
                      color: 'var(--v2-text-secondary)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {JSON.stringify(defA.definition, null, 2)}
                    </pre>
                    {versions.length > 1 && (
                      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--v2-text-muted)' }}>
                        Click any newer version above to see what changed between the two.
                      </div>
                    )}
                  </>
                )}

                {!defLoading && diff && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                      Changes from v{Math.min(selectedA.version, selectedB.version)} → v{Math.max(selectedA.version, selectedB.version)}
                    </div>
                    {diff.nodesAdded.length === 0
                      && diff.nodesRemoved.length === 0
                      && diff.nodesChanged.length === 0
                      && diff.edgesAdded.length === 0
                      && diff.edgesRemoved.length === 0 ? (
                      <div style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                        No structural changes between these versions.
                      </div>
                    ) : (
                      <>
                        <DiffSection title="Nodes added" tone="add" items={diff.nodesAdded}
                          render={(n) => <><code style={{ fontSize: 10 }}>{n.type}</code> <strong>{n.label}</strong> <span style={{ color: 'var(--v2-text-muted)' }}>({shortId(n.id)})</span></>}
                        />
                        <DiffSection title="Nodes removed" tone="remove" items={diff.nodesRemoved}
                          render={(n) => <><code style={{ fontSize: 10 }}>{n.type}</code> <strong>{n.label}</strong> <span style={{ color: 'var(--v2-text-muted)' }}>({shortId(n.id)})</span></>}
                        />
                        <DiffSection title="Nodes changed" tone="change" items={diff.nodesChanged}
                          render={(n) => <>
                            <code style={{ fontSize: 10 }}>{n.type}</code> <strong>{n.label}</strong>{' '}
                            <span style={{ color: 'var(--v2-text-muted)' }}>({shortId(n.id)})</span>
                            {n.typeChanged && <span style={{ color: 'var(--v2-accent-rose)', marginLeft: 4 }}>· type changed</span>}
                            {n.labelChanged && <span style={{ color: 'var(--v2-text-secondary)', marginLeft: 4 }}>· label changed</span>}
                          </>}
                        />
                        <DiffSection title="Edges added" tone="add" items={diff.edgesAdded}
                          render={(e) => <><span style={{ color: 'var(--v2-text-muted)' }}>{shortId(e.from)} → {shortId(e.to)}</span></>}
                        />
                        <DiffSection title="Edges removed" tone="remove" items={diff.edgesRemoved}
                          render={(e) => <><span style={{ color: 'var(--v2-text-muted)' }}>{shortId(e.from)} → {shortId(e.to)}</span></>}
                        />
                      </>
                    )}
                  </>
                )}

                {!defLoading && selectedA && defA?.__error && (
                  <div style={{ fontSize: 11, color: 'var(--v2-accent-rose)' }}>
                    {defA.__error}
                  </div>
                )}
                {!defLoading && selectedB && defB?.__error && (
                  <div style={{ fontSize: 11, color: 'var(--v2-accent-rose)' }}>
                    {defB.__error}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Slideout>
  );
}
